import type { DialogProps } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { StaticTokenCreatorTable } from "../../../../features/Modals/StaticTokenCreatorTable";
import { generateTokenUid } from "../../../../features/Modals/StaticTokenCreatorTable/helpers";
import {
  createStaticTokenAction,
  getAllStaticTokens,
} from "../../../../features/Modals/StaticTokenCreatorTable/redux/actions";
import { selectTokens } from "../../../../features/Modals/StaticTokenCreatorTable/redux/selector";
import { useAppDispatch } from "../../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../../services/redux/hooks/use-selector";
import { ModalBasic } from "../../../../shared/components/ModalBasic";
import { TModalsIds } from "../../../../app/state/modalSlice/types";
import { useModalSelectors } from "../../../../app/state/modalSlice/hook/use-modal-selectors";
import styles from "./styles.module.css";

interface Props extends Omit<DialogProps, "open"> {
  closeModal: (id: TModalsIds) => void;
}

export const StaticTokenModal = ({ closeModal, ...rest }: Props) => {
  const dispatch = useAppDispatch();
  const staticTokenModal = useModalSelectors("static_token_modal");
  const tokens = useAppSelector(selectTokens);
  const [previewToken, setPreviewToken] = useState<string>("");
  const [preview, setPreview] = useState<boolean>(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (staticTokenModal.isOpen && !tokens.length) {
      dispatch(getAllStaticTokens()).unwrap();
    }
  }, [staticTokenModal.isOpen]);

  const handleOpenPreview = () => {
    const token = `static_token_${generateTokenUid()}`;
    setPreview(true);
    setPreviewToken(token);
  };

  const handleCreateNewToken = () => {
    dispatch(createStaticTokenAction(previewToken));
    setPreview(false);
    setPreviewToken("");
  };

  const handleCancel = () => {
    if (preview) {
      setPreview(false);
    } else {
      closeModal("static_token_modal");
    }
  };

  return (
    <ModalBasic
      open={staticTokenModal.isOpen}
      title={t("static_token.modal_title")}
      hasCloseButton
      hasFooter
      onOk={handleOpenPreview}
      onClose={() => closeModal("static_token_modal")}
      closeModal={() => closeModal("static_token_modal")}
      className={styles["chat-modal"]}
      onCancel={handleCancel}
      buttonOkText={t("static_token.create_token_button")}
      disabledOkBtn={preview}
      {...rest}
    >
      <div className={styles.body}>
        <StaticTokenCreatorTable
          tokens={tokens}
          token={previewToken}
          onCreate={handleCreateNewToken}
          isPreview={preview}
        />
      </div>
    </ModalBasic>
  );
};
